import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users2, Package, Mail, Gift, UsersRound,
  ChevronsLeft, ChevronsRight, Shield, Mic, ChevronDown,
  LogOut, KeyRound, Bell, KanbanSquare, CreditCard,
} from 'lucide-react';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import NotificationPreferencesModal from '@/components/NotificationPreferencesModal';

const NAV_BY_ROLE = {
  admin: [
    { key: 'dashboard',  label: 'Dashboard',       icon: LayoutDashboard, route: '/ops/admin/dashboard' },
    { key: 'leads',      label: 'Leads',           icon: Users2,          route: '/ops/admin/leads' },
    { key: 'pipeline',   label: 'Pipeline',        icon: KanbanSquare,    route: '/ops/interviewer/pipeline' },
    { key: 'production', label: 'Production',      icon: Package,         route: '/ops/production/jobs' },
    { key: 'templates',  label: 'Email Templates', icon: Mail,            route: '/ops/admin/templates' },
    { key: 'keepsakes',  label: 'Keepsakes',       icon: Gift,            route: '/ops/admin/keepsakes' },
    { key: 'packages',   label: 'Packages',        icon: CreditCard,      route: '/ops/admin/packages' },
    { key: 'team',       label: 'Team',            icon: UsersRound,      route: '/ops/admin/team' },
  ],
  interviewer: [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, route: '/ops/interviewer/dashboard' },
    { key: 'pipeline',  label: 'Pipeline',  icon: Users2,          route: '/ops/interviewer/pipeline' },
  ],
  production: [
    { key: 'dashboard', label: 'Dashboard',  icon: LayoutDashboard, route: '/ops/production/dashboard' },
    { key: 'jobs',      label: 'Production', icon: Package,         route: '/ops/production/jobs' },
  ],
};

const ROLE_META = {
  admin:       { label: 'Admin',       icon: Shield,  route: '/ops/admin/dashboard' },
  interviewer: { label: 'Interviewer', icon: Mic,     route: '/ops/interviewer/dashboard' },
  production:  { label: 'Production',  icon: Package, route: '/ops/production/dashboard' },
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
}

export default function Sidebar({ collapsed, onToggleCollapsed, activeNav, onNavigate, role = 'admin' }) {
  const { user, activeRole, setActiveRole, logout } = useAuth();
  const navigate = useNavigate();
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const roleRef = useRef(null);
  const acctRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleMenuOpen(false);
      if (acctRef.current && !acctRef.current.contains(e.target)) setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const navItems = NAV_BY_ROLE[role] || NAV_BY_ROLE.admin;
  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.route) navigate(item.route);
    else onNavigate?.(item.key);
  };
  const handleRolePick = (r) => {
    setActiveRole(r);
    setRoleMenuOpen(false);
    navigate(ROLE_META[r].route);
  };
  const hasMultipleRoles = (user?.roles?.length || 0) > 1;
  const currentRoleMeta = ROLE_META[activeRole] || ROLE_META[user?.roles?.[0]];

  return (
    <aside
      data-testid="ops-sidebar"
      className={`${collapsed ? 'w-[60px]' : 'w-[232px]'} shrink-0 h-screen sticky top-0 flex flex-col border-r border-slate-200 bg-[#F3F5F7] transition-[width] duration-200`}
    >
      {/* Brand + collapse */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-slate-200/70">
        {!collapsed ? (
          <div className="flex items-center gap-2 pl-1.5">
            <div className="w-6 h-6 rounded-md bg-slate-900 text-white grid place-items-center text-[11px] font-semibold tracking-tight">H</div>
            <span className="text-[13px] font-semibold text-slate-900 tracking-tight">Heirloom Ops</span>
          </div>
        ) : (
          <div className="w-6 h-6 mx-auto rounded-md bg-slate-900 text-white grid place-items-center text-[11px] font-semibold">H</div>
        )}
        <button
          onClick={onToggleCollapsed}
          data-testid="sidebar-collapse-toggle"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 p-1 rounded transition-colors ${collapsed ? 'absolute right-1 top-3' : ''}`}
        >
          {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
        </button>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="ops-sidebar-nav">
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = activeNav === item.key;
          return (
            <button
              key={item.key}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              data-testid={`sidebar-nav-${item.key}`}
              className={`ops-nav-item w-full ${collapsed ? 'justify-center px-0 py-2' : ''} ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-active={isActive ? 'true' : 'false'}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={15} className="shrink-0" />
              {!collapsed && (
                <span className="truncate flex-1 text-left">{item.label}</span>
              )}
              {!collapsed && item.disabled && (
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">Soon</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Account block */}
      <div className="border-t border-slate-200/70 p-2 space-y-2">
        {/* Role switcher (only if multiple roles) */}
        {hasMultipleRoles && currentRoleMeta && (
          <div className="relative" ref={roleRef}>
            {!collapsed && (
              <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1 px-1">Viewing as</p>
            )}
            <button
              onClick={() => setRoleMenuOpen(v => !v)}
              data-testid="sidebar-role-switcher"
              className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} text-[13px] font-medium text-slate-900 bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 rounded-md px-2.5 py-2 shadow-sm transition-colors`}
              title={collapsed ? `Switch view (${currentRoleMeta.label})` : undefined}
            >
              <span className="flex items-center gap-2 min-w-0">
                <currentRoleMeta.icon size={13} className="text-slate-700 shrink-0" />
                {!collapsed && <span className="truncate">{currentRoleMeta.label} View</span>}
              </span>
              {!collapsed && <ChevronDown size={12} className={`text-slate-400 shrink-0 transition-transform ${roleMenuOpen ? 'rotate-180' : ''}`} />}
            </button>
            {roleMenuOpen && (
              <div className={`absolute bottom-full mb-1 ${collapsed ? 'left-full ml-2 w-48' : 'left-0 right-0'} z-30 bg-white border border-slate-200 rounded-md shadow-md py-1`}>
                {!collapsed && (
                  <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400 px-2.5 pt-1.5 pb-1">Switch view</p>
                )}
                {user.roles.map(r => {
                  const meta = ROLE_META[r]; if (!meta) return null;
                  const RI = meta.icon;
                  const isCurrent = r === activeRole;
                  return (
                    <button
                      key={r}
                      onClick={() => handleRolePick(r)}
                      data-testid={`sidebar-role-option-${r}`}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] ${isCurrent ? 'bg-slate-50 text-slate-900 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}
                    >
                      <RI size={12} className={isCurrent ? 'text-slate-700' : 'text-slate-500'} />
                      <span className="flex-1 text-left">{meta.label}</span>
                      {isCurrent && <span className="text-[10px] text-slate-400 uppercase tracking-wider">current</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Account */}
        <div className="relative" ref={acctRef}>
          <button
            onClick={() => setAccountMenuOpen(v => !v)}
            data-testid="sidebar-account-button"
            className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-2'} hover:bg-slate-200/40 rounded-md px-1.5 py-1.5 transition-colors`}
            title={collapsed ? user?.name : undefined}
          >
            <div className="w-7 h-7 rounded-full bg-slate-900 text-white grid place-items-center text-[10px] font-semibold shrink-0">
              {initials(user?.name)}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[12px] font-medium text-slate-900 truncate leading-tight">{user?.name}</p>
                <p className="text-[11px] text-slate-500 truncate leading-tight">{user?.email}</p>
              </div>
            )}
            {!collapsed && <ChevronDown size={11} className="text-slate-400" />}
          </button>
          {accountMenuOpen && (
            <div className={`absolute bottom-full mb-1 ${collapsed ? 'left-full ml-2 w-48' : 'left-0 right-0'} z-30 bg-white border border-slate-200 rounded-md shadow-md py-1`}>
              <button
                onClick={() => { setAccountMenuOpen(false); setShowPw(true); }}
                data-testid="sidebar-change-password"
                className="w-full flex items-center gap-2 px-2.5 py-2 text-[12px] text-slate-700 hover:bg-slate-50"
              >
                <KeyRound size={12} className="text-slate-500" /> Change password
              </button>
              <button
                onClick={() => { setAccountMenuOpen(false); setShowNotifPrefs(true); }}
                data-testid="sidebar-notification-preferences"
                className="w-full flex items-center gap-2 px-2.5 py-2 text-[12px] text-slate-700 hover:bg-slate-50"
              >
                <Bell size={12} className="text-slate-500" /> Notification preferences
              </button>
              <div className="border-t border-slate-100" />
              <button
                onClick={() => { setAccountMenuOpen(false); logout(); navigate('/ops/login'); }}
                data-testid="sidebar-logout"
                className="w-full flex items-center gap-2 px-2.5 py-2 text-[12px] text-slate-700 hover:bg-slate-50"
              >
                <LogOut size={12} className="text-slate-500" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
      {showNotifPrefs && <NotificationPreferencesModal onClose={() => setShowNotifPrefs(false)} />}
    </aside>
  );
}
